
import { LightningElement, api } from 'lwc';
import { strToInt } from '../../../utils/dtypeUtils';
import { passIcons } from '../../../utils/iconMapper';

import { 
    mapTemplateLabels, 
    TEMPLATE_LABELS, 
    getLabelsByLanguage 
} from '../../../utils/languageUtil';

const IMAGE_FILE_TYPES = [
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.heic',
    '.heif',
];



const STYLES_MEDIUM = [
    ['--image-box-font-size', 'var(--slds-g-font-scale-3)'],
    ['--image-box-width', '17rem'],
    ['--hover-icon-size', '2.5rem']
];
const STYLES_LARGE = [
    ['--image-box-font-size', 'var(--slds-g-font-scale-4)'],
    ['--image-box-width', '22rem'],
    ['--hover-icon-size', '3rem']
];

const IMAGE_ICON_CL_MEDIUM = 'slds-icon slds-icon-text-default larger';
const IMAGE_ICON_CL_LARGE = 'slds-icon slds-icon-text-default slds-icon_large';



function setFileVals(valArr) {
    if (!Array.isArray(valArr) || !valArr?.length) {
        return [];
    }
    
    const reader = new FileReader();
    reader.onload = () => {
        const previewUrl = reader.result; // data: URL (lives only in browser memory)

        return valArr.map(file => {
            if (file.name) {
                return {
                    keyId: Date.now().toString(),
                    url: previewUrl,
                    filename: file.name
                }
            }
        }).filter(Boolean);
    }
}

let KEY_ID = 7372933;
// Add <titleConfig> property to each file (titleConfig.[text/textIcon/badge/badgeIcon])
function resolveTitleFns(uploadedFiles, titlesFn) {
    if (!titlesFn || !uploadedFiles?.length) {
        return uploadedFiles;
    }
    return uploadedFiles.map((f, index) => {
        const parts = titlesFn(f, index);
        const label = parts?.label;
        if (!label) {
            return {...f, titleConfig: null};
        }   
        // Check if badge
        const isBadge = Object.keys(parts).some(p => p === 'badgeCl');
        // Check if icon
        const iconName = parts?.iconName;

        // Validate icon
        const iconParts = !iconName ? null 
            : isBadge ? resolveBadgeIcon(iconName ? `utility:${iconName}` : null)
            : passIcons(iconName ? `utility:${iconName}` : null);

        if (!iconName || !iconParts || !['container', 'icon', 'svg'].every(p => iconParts?.[p])) {
            // no icon --> return badge/text
            return isBadge
                ? {...f, titleConfig: {label, badgeCl: `slds-badge ${parts?.badgeCl ?? ''}`, keyId: Date.now().toString()}}
                : {...f, titleConfig: {label, keyId: Date.now().toString()}};
        }

        // Spread icon into badgeIcon/textIcon config
        return isBadge
            ? {...f, titleConfig: {label, ...iconParts, badgeCl: `slds-badge ${parts?.badgeCl ?? ''}`, keyId: Date.now().toString()}}
            : {...f, titleConfig: {label, ...iconParts, keyId: Date.now().toString()}};

    });
}
// Get icon for badgeIcon variant
function resolveBadgeIcon(iconName) {
    if (!iconName) return null;
    const container = `slds-icon_container slds-icon-utility-${iconName} slds-current-color`;
    const svg = 'slds-icon slds-icon_xx-small';
    const icon = `static/assets/icons/utility-sprite/svg/symbols.svg#${icon}`;
    return {container, svg, icon};
}

export default class sldsFileSelector extends LightningElement {
    templateLabels = mapTemplateLabels(TEMPLATE_LABELS);


    /* Title Variant Shape Requirements:
        👉 label --> to render a title function must return property --> label: '<label>'
        👉 badge (plain) --> function must return object with property --> {badgeCl: ''} (can be blank) 
        👉 badge (themed) -->  {badgeCl: 'slds-theme_success'} 
        👉 icon --> function must return object with property --> {iconName: '<icon>'} (only utility)
        👉 badgeIcon --> function must return object with property --> {iconName: '<icon>', badgeCl: ''}

        🟢 Ex (badge): titleFn = (file, index) => {
            return index ==== 0
                ? {badgeCl: 'slds-theme_success', label: `Primary Photo (${index+1})`}
                : {badgeCl: '', label: `${index+1}`}
        }
        🟢 Ex (icon): titleFn = (file, index) => {
            return index ==== 0
                ? {icon: 'star', label: `Primary Photo (${index+1})`}
                : {badgeCl: 'square', label: `${index+1}`}
        }
    */
    @api 
    set titlesFn(val) {
        if (val && typeof val === 'function') {
            this._titlesFn = val;
            if (this._uploadedFiles?.length) {
                this._uploadedFiles = resolveTitleFns(this._uploadedFiles, val);
            }
            this._applyVarsDirect('--border-radius-img', '0px');
            return;
        }
        this._titlesFn = null;
        this._applyVarsDirect('--border-radius-img', '0.5rem');
    }
    get titlesFn() {
        return this._titlesFn ?? null;
    }

    @api 
    set sortable(val) {
        this._sortable = val;
        if (val) {
            this._titleWrapperCl = 'img-title-wrapper slds-grid';
        }
    }
    get sortable() {
        return this._sortable;
    }

    get sortBtnCl() {
        return this._uploadedFiles?.length > 1 
            ? 'circular slds-button slds-button_icon slds-button_icon-x-small slds-button_icon-border-filled'
            : 'circular slds-button slds-button_icon slds-button_icon-x-small slds-button_icon-border-filled slds-is-disabled';
    }

    _titleWrapperCl = 'img-title-wrapper';
    @api 
    set alignTitleRight(val) {
        this._alignTitleRight = val;
        if (val) {
            this._titleWrapperCl = this.hasSort 
                ? 'img-title-wrapper slds-grid slds-p-vertical_x-small'
                : 'img-title-wrapper slds-grid slds-grid_align-end';
        } else {
            this._titleWrapperCl = 'img-title-wrapper';
        }
    }
    get alignTitleRight() {
        return this._alignTitleRight;
    }

    _isFixedImageAmt = false;
    @api 
    set label(val) {
        this._label = val ? getLabelsByLanguage(val) : getLabelsByLanguage('Attachment');
    }
    get label() {
        return this._label ?? getLabelsByLanguage('Attachment')
    }

    // Dont show images of files below if true
    @api suppressFileImages = false;
    // image, csv, etc.
    @api fileType;

    /* Variants:
        1. default: slds-file-selector w/ pills 
        2. image-box: blank tiles for each image 
        3. input-tile: default variant w/ tiles instead of pills
    */
    _imageBoxContainerCl = 'slds-p-top_medium';
    _isInputTile = false;
    _isImageBox = false;
    _isDefault = true;
    @api 
    set variant(val) {
        this._variant = val;
        this._isImageBox = val === 'image-box';
        this._isDefault = !val || val === 'regular';
        this._isInputTile = val === 'input-tile';

        if (this._isImageBox) {
            //this._applyVarsDirect('--img-padding-top', '52%');
            this._applyVarsDirect('--border-radius-img', 'var(--slds-g-radius-border-3, 0.75rem)');
            this._imageBoxContainerCl = '';
        } else {
            this._applyVarsDirect('--border-radius-img', 'var(--slds-g-radius-border-1, 0.25rem)');
        }
    }
    get variant() {
        return this._variant ?? 'regular'; 
    }

    _currentValue = [];
    _uploadedFiles = [];
    @api 
    set currentValue(val) {
        this._currentValue = val;
        if (val && Array.isArray(val) && val?.length) {
            if (!this._uploadedFiles?.length) {
                this._uploadedFiles = resolveTitleFns(val, this._titlesFn);
            } else {
                const fileNames = (this._uploadedFiles ?? [])
                    .map(f => f?.name)
                    .filter(Boolean);

                const newFiles = val?.filter(f => f?.name && !fileNames.includes(f?.name));

                if (newFiles?.length) {
                    const uploadedFiles = [...this._uploadedFiles, ...newFiles];
                    this._uploadedFiles = resolveTitleFns(uploadedFiles, this.titlesFn);
                }
            }
        }
    }
    get currentValue() {
        return this._currentValue ?? [];
    }

    // Size of the image box: small: 
    _imageBoxSize = 'small';
    _imageBoxMargin = 'slds-m-top_large'; 
    @api 
    set imageBoxSize(val) {
        switch(val) {
            case 'medium':
                this._addImageSvg = IMAGE_ICON_CL_MEDIUM
                this._imageBoxMargin = 'slds-m-top_x-large';
                this._applyVarsDirect(STYLES_MEDIUM);
                break;
            case 'large':
                this._addImageSvg = IMAGE_ICON_CL_LARGE
                this._imageBoxMargin = 'slds-m-top_xx-large'; 
                this._applyVarsDirect(STYLES_LARGE);
                break;
            default:
                break;
        }
        this._imageBoxSize = val;
    }
    get imageBoxSize() {
        return this._imageBoxSize;
    }
    _addImageSvg = 'slds-icon slds-icon-text-default';
    // Display expanded image in modal
    expandedImage;


    moveUp(event) {
        const idx = Number(event.currentTarget.dataset.index);
        const newIdx = idx - 1;
        if (newIdx < 0) return;

        const files = [...this._uploadedFiles];

        [files[idx], files[newIdx]] = [files[newIdx], files[idx]];

        this._uploadedFiles = resolveTitleFns(files, this.titlesFn);
        this._currentValue = this._uploadedFiles;
        this.dispatchFilesChange();
    }
    moveDown(event) {
        const idx = Number(event.currentTarget.dataset.index);
        const newIdx = idx + 1;
        if (newIdx >= this._uploadedFiles.length) return;

        const files = [...this._uploadedFiles];

        [files[idx], files[newIdx]] = [files[newIdx], files[idx]];

        this._uploadedFiles = resolveTitleFns(files, this.titlesFn);
        this._currentValue = this._uploadedFiles;
        this.dispatchFilesChange();
    }

    dispatchFilesChange() {
        this.dispatchEvent(new CustomEvent('change', {
            detail: this._uploadedFiles
        }));
    }


    // ================== Set fixed amount of files to upload ================== \\    
    _blankFileBoxes = [];

    @api // Min file amount  --> Show blank boxes of the min amount 
    set min(val) {
        this._min = strToInt(val);
    }
    get min() {
        return this._min;
    }
    @api // Max file amount
    set max(val) {
        this._max = strToInt(val);
    }
    get max() {
        return this._max;
    }
    // ========================================================================= \\

    
    handleMinMax() {
        if (!this._min || !this._max) return;

        const limitedFiles = [];
        for (let i=0; i < this._max; i++) {
            limitedFiles.push()
        }
    }


    get allowedFileTypes() {
        return IMAGE_FILE_TYPES 
    }
    get acceptedFileTypes() {
        return this.fileType 
            ? IMAGE_FILE_TYPES.join(',')
            : IMAGE_FILE_TYPES.join(',');
    }

    get isMax() {
        return !!this.max && this.uplo
    }

    _dropzone;
    renderedCallback() {
        if (!this._dropzone) {
            this._dropzone = this.template.querySelector('.slds-file-selector__dropzone');
        }
    }
    

    _setFile(fileList) {
        if (!fileList || fileList.length === 0) {
            return;
        }
        const file = fileList[0];
        const fileNameLower = file?.name?.toLowerCase();
        
        // extension check (your existing logic)
        if (this.allowedFileTypes && Array.isArray(this.allowedFileTypes)) {
            // check if is an accepted file type
            const isAllowed = this.allowedFileTypes.some(allowed => fileNameLower?.endsWith(allowed));

            if (!isAllowed) {
                window.alert(
                    `${file?.name} is not an allowed file type. ` +
                    `Please submit a file of one of these types: ${this.allowedFileTypes.join(', ')}`
                );
                return;
            }
        }
        

        // stage for upload
        this.selectedFile = file;
        this.uploadedFileName = file.name;

        // ✅ LOCAL PREVIEW ONLY — NO DB, NO S3
        // Only preview images
        if (file.type && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = () => {
                const previewUrl = reader.result; // data: URL (lives only in browser memory)

                const uploadedFiles = [
                    ...this._uploadedFiles,
                    {
                        keyId: Date.now().toString(),
                        url: previewUrl,
                        name: file.name, 
                        file
                    }
                ];

                this._uploadedFiles = resolveTitleFns(uploadedFiles, this.titlesFn);
                this._currentValue = this._uploadedFiles;
                
                this.dispatchFilesChange();
            };
            reader.readAsDataURL(file);
        } else {
            // not an image, clear previews
            this._uploadedFiles = [];
            this._currentValue = this._uploadedFiles;
            this.dispatchFilesChange();
        }
    }

    async _uploadFile(file) {
        this.isLoading = true;
        const formData = new FormData();
        formData.append('file', file);
        window.alert('Configure the file upload capability');
    }

    get _isAtMax() {
        return this.max && this._uploadedFiles?.length === this.max;
    }


    handleDragOver(event) {
        event.preventDefault();
        this._dropzone.classList.add('drag-over');
    }
    handleDragLeave(event) {
        event.preventDefault();
        this._dropzone.classList.remove('drag-over');
    }
    handleDrop(event) {
        event.preventDefault();
        this._dropzone.classList.remove('drag-over');
        this._setFile(event.dataTransfer.files);
    }

    // trigger the input element when the image card is clicked
    handleClickUpload(event) {
        const input = this.template.querySelector('[data-id="file-input-box"]');
        if (input) {
            //input.value = '';
            input.click();
        } 
    }
    // Handle incoming file
    handleFileChange(event) {
        this._setFile(event.target.files);
    }
    removeFile(event) {
        event.stopPropagation();
        event.preventDefault();
        this.selectedFile = null;
        this.uploadedFileName = '';
        this._uploadedFiles = []; // 🔥 hide the preview
        this._currentValue = this._uploadedFiles;
        requestAnimationFrame(() => {
            this.clearNativeFileInput();
        });
    }
    // Remove file for variants: input-tile and image-box
    removeSelectedFile(event) {
        console.log('in removeSelectedFile() of sldsFileSelector.js');
        const keyId = event.target.value;
        this.selectedFile = null;
        this.uploadedFileName = '';
        if (keyId) {
            const uploadedFiles = this._uploadedFiles.filter(f => f.keyId !== keyId);
            this._uploadedFiles = resolveTitleFns(uploadedFiles, this.titlesFn);
            this._currentValue = this._uploadedFiles;
        }
        this.dispatchFilesChange();
        requestAnimationFrame(() => {
            this.clearNativeFileInput();
        });
    }
    // Reset the input element when file is removed
    clearNativeFileInput() {
        const input = this.template.querySelector('input');
        if (input) {
            input.value = '';
        } else {
            console.log('failed to get input in clearNativeFileInput() of sldsFileSelector.js');
        }
    }

    // user clicks "Remove"
    handleRemove() {
        this.selectedFile = null;
        this.uploadedFileName = '';
    }

    closeModal() {
        this.expandedImage = null;

    }
    openImageModal(event) {
        event.preventDefault();
        const keyId = event.currentTarget.dataset.imageId;
        this.expandedImage = this._uploadedFiles.find(img => img.keyId === keyId);

        if (this.expandedImage) {
            const modal = this.template.querySelector('[data-id="image-modal"]');
            if (modal) {
                modal.openModal();
            }
        }
    }


    // user clicks "OK" to actually upload
    async handleOk() {
        if (this.isUploaded) {
            this.displayModal=false;
            this.isUploaded=false; 
            this.isErrorUploading = false;
            return;
        }
        if (!this.selectedFile) {
          return window.alert('No file selected');
        }
        await this._uploadFile(this.selectedFile);
    }
    handleCancel() {
        this.isUploaded=false; 
        this.isErrorUploading = false;
        this.displayModal=false;
    }


    // Apply Styles to the host 👉 Can pass 1.Array of Arrays   2.Array of Object   3.Strings
    _applyVarsDirect(cssVar, varStyle=null) {

        // Check that CSS var starts with '--' and has a non-null stlye 
        const isValid = (cssVarP, varStyleP) => {
            return typeof cssVarP === 'string' && 
                typeof varStyleP === 'string' &&
                cssVarP?.startsWith('--') && 
                varStyleP;
        }

        if (Array.isArray(cssVar)) {
            // Handle Array of vars
            cssVar.forEach(hostVar => {
                if (Array.isArray(hostVar) && isValid(hostVar[0], hostVar[1])) {
                    // Handle Array structure  (CSS var at index 0)
                    this.style.setProperty(hostVar[0], hostVar[1]);
                } else if (typeof hostVar === 'object' && isValid(hostVar.cssVar, hostVar.varStyle)) {
                    // Handle Object structure (cssVar & varStyle keys)
                    this.style.setProperty(hostVar.cssVar, hostVar.varStyle)
                }
            });
        } else if (isValid(cssVar, varStyle)) {
            // Handle string arguments
            this.style.setProperty(cssVar,  varStyle);
        }
    }

    // Call this function in renderedCallback  --> e.g. renderedCallback() {this.applyVars();}
    _stylesApplied = false;
    // Apply Styles to the host
    _applyVars() {
        if (this._stylesApplied) return;
        const el = this.template.host; // or a wrapper: this.template.querySelector('.root')

        if (this._sampleProperty) el.style.setProperty('--sample-property',  this._sampleProperty);

        this._stylesApplied = true;
    }

}
